/**
 * 이메일 인증 / 비밀번호 잊음 / 비밀번호 재설정 — 인증 보조 페이지 모음.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  extractErrorMessage,
  requestPasswordReset,
  resetPassword,
  verifyEmail,
} from "@/lib/api-client";

const PAGE_SHELL =
  "container flex min-h-[calc(100vh-3.5rem)] items-center justify-center py-12";
const CARD_WIDTH = "w-full max-w-sm";

/** /verify-email?token=... — 자동 검증 후 결과 표시. */
export function VerifyEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("인증 토큰이 없습니다.");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await verifyEmail(token);
        if (!cancelled) {
          setStatus("success");
          setMessage("이메일 인증이 완료됐어요.");
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          setMessage(extractErrorMessage(err, "인증에 실패했습니다."));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <main className={PAGE_SHELL}>
      <div className={`${CARD_WIDTH} text-center`}>
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">인증 중…</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h1 className="mt-4 text-2xl font-bold tracking-tight">{message}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              이제 모든 기능을 사용할 수 있어요.
            </p>
            <Link
              to="/app"
              className="mt-8 inline-flex h-10 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              대시보드로 이동
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-4 text-2xl font-bold tracking-tight">
              인증에 실패했어요
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">{message}</p>
            <p className="mt-6 text-xs text-muted-foreground">
              로그인 후 콘솔 상단의 "메일 재발송" 으로 새 인증 메일을 받을 수 있어요.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              로그인 페이지로
            </Link>
          </>
        )}
      </div>
    </main>
  );
}

/** /forgot-password — 이메일 입력 → 재설정 메일 발송. */
export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(extractErrorMessage(err, "요청에 실패했습니다."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={PAGE_SHELL}>
      <div className={CARD_WIDTH}>
        {sent ? (
          <div className="text-center">
            <Mail className="mx-auto h-12 w-12 text-primary" />
            <h1 className="mt-4 text-2xl font-bold tracking-tight">
              메일을 확인해 주세요
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              입력하신 이메일이 등록되어 있다면 비밀번호 재설정 링크를 보냈어요.
              30분 안에 메일에서 링크를 눌러 새 비밀번호를 설정해 주세요.
            </p>
            <Link
              to="/login"
              className="mt-8 inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              로그인 페이지로
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold tracking-tight">
                비밀번호를 잊으셨나요?
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                가입 시 사용한 이메일을 입력해 주세요.
              </p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className="text-sm font-medium">
                  이메일
                </label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  className="mt-1.5"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {error && (
                <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                재설정 링크 받기
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-muted-foreground">
              <Link
                to="/login"
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                로그인으로 돌아가기
              </Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
}

/** /reset-password?token=... — 새 비밀번호 입력 → 변경 + 로그인 페이지로. */
export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("재설정 토큰이 없습니다.");
      return;
    }
    if (newPassword.length < 8) {
      setError("새 비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("새 비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, newPassword);
      navigate("/login", {
        replace: true,
        state: { reason: "password-reset" },
      });
    } catch (err) {
      setError(extractErrorMessage(err, "재설정에 실패했습니다."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={PAGE_SHELL}>
      <div className={CARD_WIDTH}>
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight">새 비밀번호 설정</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            8자 이상, 안전한 비밀번호로 설정해 주세요.
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="new-password" className="text-sm font-medium">
              새 비밀번호
            </label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
              className="mt-1.5"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="text-sm font-medium">
              새 비밀번호 확인
            </label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              className="mt-1.5"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            비밀번호 변경
          </Button>
        </form>
      </div>
    </main>
  );
}
